import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Image } from "primereact/image";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";

import {
  AppDialog,
  PageHeader,
  RowActions,
  TenantGateBanner,
  emptyMessage,
  useTenantGate,
} from "../../components/ui";
import {
  useChangeBannerStatusMutation,
  useDeleteBannerMutation,
  useGetBannersQuery,
  useUpdateBannerMutation,
} from "../../store/services/banners";
import {
  BANNER_CTA_TYPE_OPTIONS,
  BANNER_STATUS_OPTIONS,
  Banner,
  MAX_ACTIVE_BANNERS,
} from "../../models/banners";
import { getAdminFacingApiErrorMessage, getApiErrorCode } from "../../utils/functions/api-errors";
import {
  API_ERROR_MESSAGES,
  bannerSortOrderSaveSteps,
  getActiveBannerCount,
  getBannerDisplayOrder,
  sortBannersByOrder,
  withReassignedBannerSortOrder,
} from "./bannerFormRules";
import { formatBannerSchedule } from "./bannerFormRules";
import { useGetHomeSectionsQuery } from "../../store/services/home-sections";
import { BannerActiveLimitDialog } from "./BannerActiveLimitDialog";
import { BannerDetailDialog, BannerStatusBadge } from "./BannerDetailDialog";
import { BannerQueryFeedback } from "./BannerQueryFeedback";
import "./banners.css";

const ctaTypeLabel = (value: string): string =>
  BANNER_CTA_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;

const canToggleBannerStatus = (status: Banner["status"]): boolean =>
  status === "active" || status === "inactive";

export const Banners = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantGate = useTenantGate();
  const tenantId = tenantGate.tenantId;
  const statusFilter = BANNER_STATUS_OPTIONS.find(
    (option) => option.value === searchParams.get("status")
  )?.value ?? null;

  const { currentData: data, isLoading, isFetching, isError, error, refetch } = useGetBannersQuery(
    { tenantId },
    { skip: !tenantGate.allowed, pollingInterval: 30000, skipPollingIfUnfocused: true }
  );

  /**
   * El intervalo del carrusel vive en `home_section`, no en el banner, y se edita en
   * Configuracion > Secciones del Home. Se muestra aca porque es el dato que falta para entender
   * cada cuanto rota lo que uno esta cargando.
   */
  const { data: homeSections } = useGetHomeSectionsQuery(
    { tenantId: tenantId ?? "" },
    { skip: !tenantGate.allowed || !tenantId }
  );
  const carouselIntervalSeconds = homeSections?.find(
    (section) => section.sectionType === "banners_carousel"
  )?.carouselIntervalSeconds;
  const [changeStatus] = useChangeBannerStatusMutation();
  const [deleteBanner, { isLoading: isDeleting }] = useDeleteBannerMutation();
  const [updateBanner] = useUpdateBannerMutation();
  const [showActiveLimit, setShowActiveLimit] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [togglingId, setTogglingId] = useState<string>();
  const [confirmedToggle, setConfirmedToggle] = useState<Pick<Banner, "id" | "status">>();
  /** Hay un refetch en vuelo disparado por el guardado del orden: manda lo optimista. */
  const [orderRefetchPending, setOrderRefetchPending] = useState(false);
  const [bannerToView, setBannerToView] = useState<Banner>();
  const [bannerToDelete, setBannerToDelete] = useState<Banner>();
  const [rows, setRows] = useState<Banner[]>([]);

  const banners = useMemo(() => data ?? [], [data]);
  const visibleBanners = useMemo(() => {
    const ordered = sortBannersByOrder(banners);
    return statusFilter ? ordered.filter((banner) => banner.status === statusFilter) : ordered;
  }, [banners, statusFilter]);
  const activeCount = useMemo(() => getActiveBannerCount(banners), [banners]);

  /**
   * Las filas optimistas del arrastre se conservan hasta que llega dato FRESCO.
   *
   * `updateBanner` invalida la lista, asi que RTK dispara un refetch — pero `unwrap()` resuelve
   * cuando termina el PATCH, no ese refetch. Sin esto, `isSavingOrder` pasaba a false con el
   * refetch en vuelo y las filas se reescribian con el dato viejo del cache: la fila volvia sola
   * a su lugar anterior y recien saltaba al nuevo cuando aterrizaba el refetch.
   *
   * La espera se limita al refetch QUE DISPARO EL GUARDADO (`orderRefetchPending`). Mirar
   * `isFetching` a secas rompia la carga: al entrar a la pantalla con dato en cache,
   * `refetchOnMountOrArgChange` deja `isFetching` en true con `data` ya disponible, y la tabla
   * se renderizaba VACIA hasta que volviera el request.
   */
  useEffect(() => {
    if (isSavingOrder) return;
    if (orderRefetchPending) {
      if (isFetching) return;
      setOrderRefetchPending(false);
    }
    setRows(visibleBanners);
  }, [visibleBanners, isSavingOrder, isFetching, orderRefetchPending]);

  useEffect(() => {
    if (statusFilter === "active") setShowActiveLimit(false);
  }, [statusFilter]);

  useEffect(() => {
    if (!confirmedToggle) return;
    const row = rows.find((banner) => banner.id === confirmedToggle.id);
    if (row && row.status !== confirmedToggle.status) return;
    setTogglingId(undefined);
    setConfirmedToggle(undefined);
  }, [rows, confirmedToggle]);

  const persistOrder = async (nextRows: Banner[]): Promise<void> => {
    const sequenced = withReassignedBannerSortOrder(nextRows);
    const steps = bannerSortOrderSaveSteps(rows, sequenced);
    if (steps.length === 0) return;
    setRows(sequenced);
    setIsSavingOrder(true);
    try {
      for (const step of steps) {
        await Promise.all(
          step.map((patch) =>
            updateBanner({ tenantId, id: patch.id, body: { sortOrder: patch.sortOrder } }).unwrap()
          )
        );
      }
      setOrderRefetchPending(true);
    } catch (error) {
      toast.error(
        getApiErrorCode(error) === "BANNER_SORT_ORDER_TAKEN"
          ? API_ERROR_MESSAGES.BANNER_SORT_ORDER_TAKEN
          : "No se pudo actualizar el orden"
      );
      // Fallo: el servidor es la autoridad, se descarta lo optimista.
      setOrderRefetchPending(false);
      void refetch();
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleRowReorder = (event: DataTableRowReorderEvent<Banner[]>): void => {
    void persistOrder(event.value);
  };

  const handleToggleStatus = async (banner: Banner, nextActive: boolean): Promise<void> => {
    if (!canToggleBannerStatus(banner.status) || togglingId || isSavingOrder || orderRefetchPending) return;
    setTogglingId(banner.id);
    try {
      const savedBanner = await changeStatus({
        tenantId,
        id: banner.id,
        status: nextActive ? "active" : "inactive",
      }).unwrap();
      setConfirmedToggle({ id: savedBanner.id, status: savedBanner.status });
      toast.success(nextActive ? "Banner activado" : "Banner desactivado");
    } catch (error) {
      setTogglingId(undefined);
      if (getApiErrorCode(error) === "MAX_ACTIVE_BANNERS_REACHED") {
        setShowActiveLimit(true);
        return;
      }
      toast.error(getAdminFacingApiErrorMessage(error, "No se pudo guardar el banner"));
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!bannerToDelete) return;
    try {
      await deleteBanner({ tenantId, id: bannerToDelete.id }).unwrap();
      setBannerToDelete(undefined);
      toast.success("Banner eliminado");
    } catch (error) {
      toast.error(getAdminFacingApiErrorMessage(error, "No se pudo eliminar el banner"));
    }
  };

  return (
    <div className="p-card banners-page">
      <PageHeader
        title="Banners del Home"
        onCreate={tenantGate.allowed ? () => navigate("/config/home-banners/new") : undefined}
        createLabel="Crear banner"
      />

      <TenantGateBanner action="ver y crear banners" gate={tenantGate} />

      {tenantGate.allowed && (
        <>
          <div className="flex flex-wrap justify-content-end align-items-center gap-3 mb-3">
            <div className="flex align-items-center gap-2 mr-auto">
              <label htmlFor="banner-status-filter" className="text-sm font-semibold text-600">
                Estado
              </label>
              <Dropdown
                inputId="banner-status-filter"
                value={statusFilter}
                options={[{ label: "Todos", value: null }, ...BANNER_STATUS_OPTIONS]}
                optionLabel="label"
                optionValue="value"
                placeholder="Todos"
                showClear
                disabled={isLoading || isSavingOrder || orderRefetchPending}
                onChange={(event) => {
                  setSearchParams((currentParams) => {
                    const nextParams = new URLSearchParams(currentParams);
                    if (event.value) nextParams.set("status", event.value);
                    else nextParams.delete("status");
                    return nextParams;
                  });
                }}
                style={{ minWidth: "12rem" }}
              />
            </div>
            {/* AGENTS.md, "Loading UX": mientras se revalida un dato YA VISIBLE va un indicador no
                bloqueante. El overlay de la tabla queda solo para la carga fria. */}
            {isFetching && !isLoading && !isSavingOrder && (
              <span
                className="inline-flex align-items-center gap-2 text-color-secondary text-sm"
                role="status"
                aria-live="polite"
              >
                <ProgressSpinner style={{ width: "16px", height: "16px" }} strokeWidth="6" />
                Actualizando…
              </span>
            )}
            {carouselIntervalSeconds !== undefined && (
              <Tag
                severity="info"
                icon="pi pi-clock"
                value={`Rota cada ${carouselIntervalSeconds}s`}
                data-pr-tooltip="Se configura en Configuración > Secciones del Home"
              />
            )}
            {data && !isError && (
              <Tag
                severity={activeCount >= MAX_ACTIVE_BANNERS ? "warning" : "info"}
                value={`${activeCount}/${MAX_ACTIVE_BANNERS} activos`}
              />
            )}
          </div>

          <DataTable
            value={isError ? [] : rows}
            cellMemo={false}
            /**
             * NO `isFetching`. Ese overlay es bloqueante: `refetchOnFocus` esta prendido a nivel
             * global, asi que cualquier vuelta del foco al navegador disparaba un refetch y la
             * tabla se tapaba con una mascara ENCIMA de la fila que estabas arrastrando — el
             * gesto se perdia y parecia que el reordenamiento no funcionaba. Es el antipatron
             * que el AGENTS.md marca ("atarlo a isFetching tapa datos ya disponibles en cada
             * refetch"). Carga fria y guardado de orden si bloquean; revalidar en segundo plano
             * muestra el indicador de arriba.
             */
            loading={isLoading || isSavingOrder}
            dataKey="id"
            emptyMessage={isError ? (
              <BannerQueryFeedback
                isError={isError}
                isFetching={isFetching}
                hasData={!!data}
                error={error}
                onRetry={() => void refetch()}
              />
            ) : emptyMessage("banners")}
            reorderableRows={!isSavingOrder}
            onRowReorder={handleRowReorder}
          >
            <Column rowReorder align="center" alignHeader="center" style={{ width: "3rem" }} />
            <Column
              header="Orden"
              align="center"
              alignHeader="center"
              headerClassName="banner-order-col"
              bodyClassName="banner-order-col"
              style={{ width: "5rem" }}
              body={getBannerDisplayOrder}
            />
            <Column
              header="Imagen"
              body={(banner: Banner) =>
                banner.imageUrl ? (
                  <Image
                    src={banner.imageUrl}
                    alt={banner.name}
                    imageClassName="banners-thumbnail"
                    preview
                    pt={{ preview: { className: "banner-image-lightbox" } }}
                  />
                ) : null
              }
            />
            <Column
              field="name"
              header="Nombre"
              body={(banner: Banner) => <span className="banners-cell">{banner.name}</span>}
            />
            <Column
              header="Texto corto"
              body={(banner: Banner) => <span className="banners-cell">{banner.shortText ?? "—"}</span>}
            />
            <Column header="Acción" body={(banner: Banner) => ctaTypeLabel(banner.ctaType)} />
            <Column
              header="Vigencia"
              body={(banner: Banner) => formatBannerSchedule(banner)}
            />
            {/* Switch, no un ícono de ojo en la columna de acciones: en el resto del Backoffice
                el ojo abre el detalle (Beneficios, Partners, Categorías), así que usándolo para
                activar/desactivar el mismo ícono significaba dos cosas distintas. El toggle
                ON/OFF es el control auditado del §7 y el patrón que ya usa Categorías de
                beneficios para exactamente esto. */}
            <Column
              header="Estado"
              style={{ minWidth: "6rem" }}
              body={(banner: Banner) => (
                <div className="flex align-items-center gap-2">
                  <InputSwitch
                    checked={banner.status === "active"}
                    disabled={!canToggleBannerStatus(banner.status) || Boolean(togglingId) || isSavingOrder || orderRefetchPending}
                    aria-label={`Estado de ${banner.name}`}
                    onChange={(event: InputSwitchChangeEvent) =>
                      void handleToggleStatus(banner, event.value === true)
                    }
                  />
                  <BannerStatusBadge status={banner.status} />
                </div>
              )}
            />
            <Column
              header="Acciones"
              body={(banner: Banner) => (
                <RowActions
                  actions={[
                    {
                      icon: "pi pi-eye",
                      label: "Ver",
                      severity: "info",
                      onClick: () => setBannerToView(banner),
                    },
                    {
                      icon: "pi pi-pencil",
                      label: "Editar",
                      onClick: () => navigate(`/config/home-banners/${banner.id}/edit`),
                    },
                    {
                      icon: "pi pi-trash",
                      label: "Eliminar",
                      severity: "danger",
                      onClick: () => setBannerToDelete(banner),
                    },
                  ]}
                />
              )}
            />
          </DataTable>
        </>
      )}

      <BannerActiveLimitDialog visible={showActiveLimit} onHide={() => setShowActiveLimit(false)} />
      <BannerDetailDialog tenantId={tenantId} banner={bannerToView} onHide={() => setBannerToView(undefined)} />
      <AppDialog
        variant="destructive"
        visible={Boolean(bannerToDelete)}
        onHide={() => setBannerToDelete(undefined)}
        header="Eliminar banner"
        primaryLabel="Sí, eliminar"
        onPrimary={() => void confirmDelete()}
        loading={isDeleting}
      >
        <p className="m-0">
          Se va a eliminar <strong>{bannerToDelete?.name}</strong>. La landing page asociada, si
          tiene, se elimina con él. No se puede deshacer.
        </p>
      </AppDialog>
    </div>
  );
};
